/**
 * A partir de `matchDetailsData` (nivel `data` del servicio): `players` y `matchCard[0]`
 * para alinear local / visitante, nombres e ítems por posición.
 */

import {
  eventMatchesAway,
  eventMatchesHome,
  getMatchTeamRefs,
} from '../../utils/matchTeamRefUtils';

const SECTION_DEF = { id: 'def', title: 'Defensas' };
const SECTION_MID = { id: 'mid', title: 'Mediocampistas' };
const SECTION_FWD = { id: 'fwd', title: 'Delanteros' };
const SECTION_GK = { id: 'gk', title: 'Portero' };

function strVal(v) {
  if (v == null || v === '') return '0';
  return String(v);
}

/**
 * Stats alineadas con `FULL_TABLE_STAT_KEYS` / tabla completa (G … TAC).
 * Campos API conocidos (mock/servicio); el resto cae en `strVal` → "0".
 */
function mapOutfieldStatistics(s = {}) {
  const ppa = s.passAccuracy;
  const ppaStr = ppa == null || ppa === '' ? '0%' : `${String(ppa).replace(/%$/, '')}%`;
  return {
    G: strVal(s.goals),
    PAG: strVal(s.assists),
    TT: strVal(s.shotsTotal),
    PT: strVal(s.passesTotal),
    EG: strVal(s.tacklesWon),
    FJ: strVal(s.offsides),
    RE: s.successfulDribbles != null ? strVal(s.successfulDribbles) : '0',
    TAP: strVal(s.shotsOnGoal),
    TF: s.shotsWide != null ? strVal(s.shotsWide) : '0',
    TB: s.shotsBlocked != null ? strVal(s.shotsBlocked) : '0',
    PPA: ppaStr,
    C: strVal(s.crosses ?? s.centers ?? s.centersTotal),
    I: strVal(s.interceptions),
    B: strVal(s.blocks ?? s.clearances ?? s.blocked),
    FC: strVal(s.fouls ?? s.foulsCommitted),
    FR: strVal(s.foulsDrawn ?? s.foulsSuffered ?? s.wasFouled),
    PP: strVal(s.saves),
    TAC: strVal(s.shotsOnTargetAgainst ?? s.shotsConcededOnTarget),
  };
}

function mapOutfieldPlayer(p) {
  if (!p?.playerId) return null;
  return {
    id: p.playerId,
    name: p.name ?? '',
    number: p.number,
    photo: p.photo,
    stats: mapOutfieldStatistics(p.statistics || {}),
  };
}

function mapGkPlayer(p) {
  if (!p?.playerId) return null;
  const st = p.statistics || {};
  return {
    id: p.playerId,
    name: p.name ?? '',
    number: p.number,
    photo: p.photo,
    stats: {
      PP: strVal(st.saves),
      TAC: strVal(st.shotsOnTargetAgainst),
    },
  };
}

function rosterToSections(block) {
  if (!block) {
    return null;
  }
  const mapList = (arr, mapFn) =>
    (Array.isArray(arr) ? arr : [])
      .map((row) => mapFn(row))
      .filter(Boolean);

  const def = mapList(block.defenders, mapOutfieldPlayer);
  const mid = mapList(block.midfielders, mapOutfieldPlayer);
  const fwd = mapList(block.forwards, mapOutfieldPlayer);
  const gks = mapList(block.goalkeepers, mapGkPlayer);

  return [
    { ...SECTION_DEF, kind: 'outfield', players: def },
    { ...SECTION_MID, kind: 'outfield', players: mid },
    { ...SECTION_FWD, kind: 'outfield', players: fwd },
    { ...SECTION_GK, kind: 'gk', players: gks },
  ];
}

function emptyRosterSections() {
  return [
    { ...SECTION_DEF, kind: 'outfield', players: [] },
    { ...SECTION_MID, kind: 'outfield', players: [] },
    { ...SECTION_FWD, kind: 'outfield', players: [] },
    { ...SECTION_GK, kind: 'gk', players: [] },
  ];
}

export function mapMatchDetailsToJugadoresData(matchDetailsData) {
  const rosters = matchDetailsData?.players;
  if (!Array.isArray(rosters) || rosters.length === 0) return null;

  const card = matchDetailsData?.matchCard?.[0];
  const md = card?.summary?.matchDetails;
  const homeId = md?.home?.team?.teamId;
  const awayId = md?.away?.team?.teamId;
  const homeTeam = md?.home?.team;
  const awayTeam = md?.away?.team;
  const refs = getMatchTeamRefs(md);

  const byHome = rosters.find((t) => eventMatchesHome(t.teamId, refs));
  const byAway = rosters.find((t) => eventMatchesAway(t.teamId, refs));
  const otherThan = (b) => rosters.find((t) => t.teamId !== b?.teamId);

  const homeBlock = byHome ?? rosters[0];
  const awayBlock =
    byAway ??
    otherThan(homeBlock) ??
    (rosters[1] && rosters[1] !== rosters[0] ? rosters[1] : null);

  if (!homeBlock) return null;

  const hSec = rosterToSections(homeBlock);
  if (!hSec) return null;

  const aSec = awayBlock ? rosterToSections(awayBlock) : emptyRosterSections();

  return {
    homeSections: hSec,
    awaySections: aSec,
    homeName: homeTeam?.name || 'Local',
    awayName: awayTeam?.name || 'Visitante',
    // Pastillas móvil/tablet: siglas ISO (`code`, p. ej. MEX/POR); `teamId` GN* como respaldo.
    homeCode: homeTeam?.code || homeTeam?.teamId || (homeId ?? 'H'),
    awayCode: awayTeam?.code || awayTeam?.teamId || (awayId ?? 'A'),
    homeFlagUrl: homeTeam?.flagUrl,
    awayFlagUrl: awayTeam?.flagUrl,
  };
}
